import { BaseModel } from './base.model';
import { environment } from '@env/environment';
import { Image } from './image.model';
import { Unit } from './unit.model';

const SUPPORTED_LANGUAGES = ['en', 'de', 'it'] as const;

export class Instance extends BaseModel {
  id!: number;
  uuid?: string;
  name!: string;
  coverImageId?: number;
  logoImageId?: number;
  faviconImageId?: number;
  config: any;
  featureConfig?: string[];
  applicationFormConfig?: any;
  shortlistFormConfig?: any;
  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;

  logoImage?: Image;
  faviconImage?: Image;
  coverImage?: Image;
  logoMarkup?: string | null;
  units?: Unit[];
  isCrifCheckAvailable?: boolean;
  isUnitSyncEnabled?: boolean;

  override get childModels() {
    return { units: Unit };
  }

  get defaultLanguage(): 'en' | 'de' | 'it' {
    const lang = this.config?.defaultLanguage;
    return SUPPORTED_LANGUAGES.includes(lang) ? lang : 'de';
  }

  get applicationFormLanguages(): Array<'en' | 'de' | 'it'> {
    const configured: unknown = this.config?.applicationFormLanguages;
    const languages = Array.isArray(configured)
      ? SUPPORTED_LANGUAGES.filter(code => configured.includes(code))
      : [];
    if (languages.length) {
      return languages;
    }

    const defaultLang = this.config?.defaultLanguage;
    return SUPPORTED_LANGUAGES.includes(defaultLang) ? [defaultLang] : [...SUPPORTED_LANGUAGES];
  }

  get applicationFormInitialLanguage(): 'en' | 'de' | 'it' {
    const languages = this.applicationFormLanguages;
    return languages.includes(this.defaultLanguage) ? this.defaultLanguage : languages[0];
  }

  get coverImageUrl(): string | null {
    if (this.coverImageId) {
      return `${environment.apiBaseUrl}/${this.id}/images/${this.coverImageId}`;
    };

    if (this.coverImage) {
      return this.coverImage.url;
    }

    return null;
  }

  get logoImageUrl(): string | null {
    if (this.logoImageId) {
      return `${environment.apiBaseUrl}/${this.id}/images/${this.logoImageId}`;
    };

    if (this.logoImage) {
      return this.logoImage.url;
    }

    return null;
  }

  get faviconImageUrl(): string | null {
    if (this.faviconImageId) {
      return `${environment.apiBaseUrl}/${this.id}/images/${this.faviconImageId}`;
    };

    if (this.faviconImage) {
      return this.faviconImage.url;
    }

    return null;
  }

  get hasApplicationForm(): boolean {
    const config = this.applicationFormConfig;

    if (!config) {
      return false;
    }

    return Unit.allTypes().some(type => config[type] != null);
  }

  hasFeature(key: string): boolean {
    return this.featureConfig?.includes(key) ?? false;
  }

  get isRentalManagerActive(): boolean {
    return this.hasFeature('rental_manager');
  }

  get isObjectFinderActive(): boolean {
    return this.hasFeature('object_finder');
  }
}
